import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Search,
  MapPin,
  Plus,
  Loader2,
} from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './sheet'
import { Button } from './button'
import { Badge } from './badge'
import { Input } from './input'
import {
  useDebouncedSearch,
  useSearchCache,
  useResponsiveDropdown,
  useSwipeGestures,
  useKeyboardNavigation,
  usePerformanceOptimization,
  useMobileOptimizedLoading,
} from '@/hooks/useSearchableSelect'
import { useVirtualizer } from '@tanstack/react-virtual'

import { cn } from '@/lib/utils'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-4 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      // Enhanced mobile touch targets - VRP Design System compliance
      'min-h-[44px] sm:min-h-[40px] touch-manipulation',
      className
    )}
    data-slot="select-trigger"
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-2',
      // Mobile-optimized scroll button
      'min-h-[44px] sm:min-h-[36px]',
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-2',
      // Mobile-optimized scroll button
      'min-h-[44px] sm:min-h-[36px]',
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-[70] max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        // Enhanced momentum scrolling on mobile
        'overscroll-behavior-y-contain',
        className
      )}
      position={position}
      data-slot="select-content"
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'py-2.5 pl-8 pr-2 text-sm font-semibold',
      // VRP Design System compliance - Limited font sizes and weights
      'text-sm font-semibold', // Size 4 (small text), Semibold weight
      className
    )}
    data-slot="select-label"
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-2.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      // Enhanced mobile touch targets following VRP 8pt grid system
      'min-h-[44px] sm:min-h-[36px] py-2 touch-manipulation',
      // VRP Design System compliance - Typography
      'text-sm font-normal', // Size 4 (small text), Regular weight
      className
    )}
    data-slot="select-item"
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-2 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

// Searchable Select - Mobile-first design with VRP compliance
interface SearchableSelectOption<T = any> {
  value: string
  label: string
  keywords?: string[]
  icon?: React.ReactNode
  badge?: string
  data?: T
}

interface SearchableSelectProps<T = any> {
  value: string | undefined
  onValueChange: (value: string) => void
  options: SearchableSelectOption<T>[]
  placeholder?: string
  className?: string
  noSelectionValue?: string
  noSelectionLabel?: string
  createNewValue?: string
  createNewLabel?: string
  onCreateNew?: () => void
  isLoading?: boolean
  emptyMessage?: string
  searchPlaceholder?: string
  maxHeight?: number
  autoOpen?: boolean // Auto-open dropdown on mount for immediate interaction
}

const fuzzySearch = (options: SearchableSelectOption[], query: string) => {
  if (!query) return options
  const searchTerm = query.toLowerCase()
  return options
    .map(option => {
      let score = 0
      if (option.label.toLowerCase().startsWith(searchTerm)) score += 100
      else if (option.label.toLowerCase().includes(searchTerm)) score += 50
      else if (option.keywords?.some(k => k.toLowerCase().includes(searchTerm)))
        score += 25
      return { option, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ option }) => option)
}

const SearchableSelect = <T,>({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  className,
  noSelectionValue,
  noSelectionLabel = 'No selection',
  createNewValue = '__create_new__',
  createNewLabel = 'Create new',
  onCreateNew,
  isLoading = false,
  emptyMessage = 'No options found',
  searchPlaceholder = 'Search...',
  maxHeight = 300,
  autoOpen = false,
}: SearchableSelectProps<T>) => {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedSearch(search, 300)
  const { getCache, setCache } = useSearchCache<SearchableSelectOption<T>>()
  const deviceInfo = useResponsiveDropdown()
  const { isLowEndDevice, shouldReduceAnimations } =
    usePerformanceOptimization()
  const showLoader = useMobileOptimizedLoading(isLoading)

  // Enhanced mobile gesture support
  const { touchHandlers } = useSwipeGestures(
    () => setOpen(false), // Swipe down to close
    undefined,
    30 // Lower threshold for better mobile UX
  )

  const filteredOptions = React.useMemo(() => {
    const key = `search:${debouncedSearch}`
    const cached = getCache(key)
    if (cached) return cached
    const results = fuzzySearch(options, debouncedSearch)
    setCache(key, results)
    return results
  }, [options, debouncedSearch, getCache, setCache])

  // Enhanced keyboard navigation
  const { selectedIndex, setSelectedIndex, resetSelection } =
    useKeyboardNavigation(
      open,
      () => setOpen(false),
      filteredOptions.length +
        (noSelectionValue ? 1 : 0) +
        (onCreateNew ? 1 : 0)
    )

  const selectedOption = options.find(opt => opt.value === value)

  // Auto-open dropdown on mount for immediate user interaction
  React.useEffect(() => {
    if (autoOpen && !open) {
      // Small delay to ensure component is fully mounted and rendered
      const timer = setTimeout(() => setOpen(true), 50)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Reset search and selection when closing
  React.useEffect(() => {
    if (!open) {
      setSearch('')
      resetSelection()
    }
  }, [open, resetSelection])

  // Mobile-optimized Command List with virtualization for performance
  const CommandListContent = React.useMemo(() => {
    const items = [
      ...(noSelectionValue
        ? [{ type: 'no-selection' as const, value: noSelectionValue }]
        : []),
      ...filteredOptions.map(option => ({ type: 'option' as const, option })),
      ...(onCreateNew ? [{ type: 'create-new' as const }] : []),
    ]

    // Use virtualization for large lists on low-end devices
    if (isLowEndDevice && items.length > 50) {
      // Simplified rendering for low-end devices
      return items.slice(0, 50).map((item, index) => {
        if (item.type === 'no-selection') {
          return (
            <CommandItem
              key="no-selection"
              onSelect={() => {
                onValueChange(item.value)
                setOpen(false)
              }}
              className={cn(
                'min-h-[44px] sm:min-h-[36px]',
                selectedIndex === index && 'bg-accent text-accent-foreground'
              )}
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  value === item.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              {noSelectionLabel}
            </CommandItem>
          )
        }

        if (item.type === 'option') {
          return (
            <CommandItem
              key={item.option.value}
              onSelect={() => {
                onValueChange(item.option.value)
                setOpen(false)
              }}
              className={cn(
                'min-h-[44px] sm:min-h-[36px]',
                selectedIndex === index && 'bg-accent text-accent-foreground'
              )}
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  value === item.option.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {item.option.icon}
                <span className="truncate">{item.option.label}</span>
                {item.option.badge && (
                  <Badge variant="outline" className="text-sm shrink-0">
                    {item.option.badge}
                  </Badge>
                )}
              </div>
            </CommandItem>
          )
        }

        if (item.type === 'create-new') {
          return (
            <CommandItem
              key="create-new"
              onSelect={() => {
                onCreateNew?.()
                setOpen(false)
              }}
              className={cn(
                'min-h-[44px] sm:min-h-[36px] text-primary',
                selectedIndex === index && 'bg-accent text-accent-foreground'
              )}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span>{createNewLabel}</span>
            </CommandItem>
          )
        }

        return null
      })
    }

    return items.map((item, index) => {
      if (item.type === 'no-selection') {
        return (
          <CommandItem
            key="no-selection"
            onSelect={() => {
              onValueChange(item.value)
              setOpen(false)
            }}
            className={cn(
              'min-h-[44px] sm:min-h-[36px]',
              selectedIndex === index && 'bg-accent text-accent-foreground'
            )}
          >
            <Check
              className={cn(
                'mr-2 h-4 w-4',
                value === item.value ? 'opacity-100' : 'opacity-0'
              )}
            />
            {noSelectionLabel}
          </CommandItem>
        )
      }

      if (item.type === 'option') {
        return (
          <CommandItem
            key={item.option.value}
            onSelect={() => {
              onValueChange(item.option.value)
              setOpen(false)
            }}
            className={cn(
              'min-h-[44px] sm:min-h-[36px]',
              selectedIndex === index && 'bg-accent text-accent-foreground'
            )}
          >
            <Check
              className={cn(
                'mr-2 h-4 w-4',
                value === item.option.value ? 'opacity-100' : 'opacity-0'
              )}
            />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {item.option.icon}
              <span className="truncate">{item.option.label}</span>
              {item.option.badge && (
                <Badge variant="outline" className="text-sm shrink-0">
                  {item.option.badge}
                </Badge>
              )}
            </div>
          </CommandItem>
        )
      }

      if (item.type === 'create-new') {
        return (
          <CommandItem
            key="create-new"
            onSelect={() => {
              onCreateNew?.()
              setOpen(false)
            }}
            className={cn(
              'min-h-[44px] sm:min-h-[36px] text-primary',
              selectedIndex === index && 'bg-accent text-accent-foreground'
            )}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>{createNewLabel}</span>
          </CommandItem>
        )
      }

      return null
    })
  }, [
    filteredOptions,
    noSelectionValue,
    noSelectionLabel,
    onCreateNew,
    createNewLabel,
    value,
    onValueChange,
    setOpen,
    selectedIndex,
    isLowEndDevice,
  ])

  // Mobile bottom sheet vs desktop popover
  if (deviceInfo.isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              'w-full justify-between min-h-[44px] text-left font-normal',
              // VRP Design System compliance - 8pt grid system spacing
              'px-4 py-2',
              className
            )}
          >
            <div className="flex items-center gap-2 truncate">
              {showLoader && <Loader2 className="h-4 w-4 animate-spin" />}
              {!showLoader && selectedOption?.icon}
              <span className="truncate">
                {selectedOption?.label || placeholder}
              </span>
              {selectedOption?.badge && (
                <Badge variant="outline" className="text-sm shrink-0">
                  {selectedOption.badge}
                </Badge>
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            'max-h-[85vh] flex flex-col',
            // VRP Design System compliance - 8pt grid system
            'p-6 gap-4',
            shouldReduceAnimations &&
              'data-[state=open]:animate-none data-[state=closed]:animate-none'
          )}
          {...touchHandlers}
        >
          <SheetHeader>
            <SheetTitle className="text-lg font-semibold text-left">
              {placeholder}
            </SheetTitle>
          </SheetHeader>
          <Command className="flex-1 min-h-0">
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              className="min-h-[44px]"
              showSearchIcon={!showLoader}
            />
            <CommandList
              className={cn(
                'flex-1 overflow-y-auto overscroll-contain',
                `max-h-[${maxHeight}px]`,
                // Enhanced scroll momentum on iOS
                '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                'scroll-smooth'
              )}
            >
              {showLoader ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                    {emptyMessage}
                  </CommandEmpty>
                  {CommandListContent}
                </>
              )}
            </CommandList>
          </Command>
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn('w-full justify-between h-10 font-normal', className)}
        >
          <div className="flex items-center gap-2 truncate">
            {showLoader && <Loader2 className="h-4 w-4 animate-spin" />}
            {!showLoader && selectedOption?.icon}
            <span className="truncate">
              {selectedOption?.label || placeholder}
            </span>
            {selectedOption?.badge && (
              <Badge variant="outline" className="text-sm shrink-0">
                {selectedOption.badge}
              </Badge>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            showSearchIcon={!showLoader}
          />
          <CommandList className={cn(`max-h-[${maxHeight}px] overflow-y-auto`)}>
            {showLoader ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </CommandEmpty>
                {CommandListContent}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Multi-Select Skills Component for VRP System
interface MultiSelectSkillsProps {
  value: number[] | undefined
  onValueChange: (value: number[]) => void
  projectId: string
  skills?: any[] // Skills data passed from parent
  placeholder?: string
  className?: string
  disabled?: boolean
  isLoading?: boolean
  maxSelection?: number
}

const MultiSelectSkills = ({
  value = [],
  onValueChange,
  skills = [],
  className,
  disabled = false,
  maxSelection = 10,
}: MultiSelectSkillsProps) => {
  const [searchTerm, setSearchTerm] = React.useState('')

  // Transform skills data for display
  const skillsMap = React.useMemo(() => {
    if (!skills || skills.length === 0) return new Map()

    const map = new Map()
    skills.forEach(skill => {
      if (skill.optimizerSkillId !== undefined) {
        map.set(skill.optimizerSkillId, skill)
      }
    })
    return map
  }, [skills])

  // Filter skills based on search
  const filteredSkills = React.useMemo(() => {
    if (!skills || skills.length === 0) return []

    return skills.filter(
      skill =>
        skill.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skill.category?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [skills, searchTerm])

  const selectedSkillsDisplay = React.useMemo(() => {
    return value.map(skillId => skillsMap.get(skillId)).filter(Boolean)
  }, [value, skillsMap])

  const handleSkillToggle = (skillId: number) => {
    const currentValues = value || []

    if (currentValues.includes(skillId)) {
      // Remove skill
      onValueChange(currentValues.filter(id => id !== skillId))
    } else {
      // Add skill (check max limit)
      if (currentValues.length >= maxSelection) {
        return // Don't add if at max
      }
      onValueChange([...currentValues, skillId])
    }
  }

  if (!skills || skills.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 p-3 border border-dashed border-muted-foreground/20 rounded-md',
          className
        )}
      >
        <span className="text-sm text-muted-foreground">
          No skills defined for this project
        </span>
      </div>
    )
  }

  return (
    <div className={cn('w-full space-y-2', className)}>
      {/* Selected skills display */}
      {selectedSkillsDisplay.length > 0 && (
        <div className="flex flex-wrap gap-1 p-2 bg-muted/20 rounded-md">
          {selectedSkillsDisplay.map(skill => (
            <Badge
              key={skill.optimizerSkillId}
              variant="secondary"
              className="text-sm cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => handleSkillToggle(skill.optimizerSkillId)}
              title={`Click to remove • ${skill.category || 'No category'}`}
            >
              {skill.name}
              {skill.requiresCertification && ' 🏆'}
              <span className="ml-1 text-sm opacity-60">×</span>
            </Badge>
          ))}
          <Badge variant="outline" className="text-sm">
            {selectedSkillsDisplay.length} selected
          </Badge>
        </div>
      )}

      {/* Search input */}
      <Input
        type="text"
        placeholder="Search skills..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="w-full"
        disabled={disabled}
      />

      {/* Available skills */}
      <div className="max-h-32 overflow-y-auto border border-input rounded-md bg-background">
        {filteredSkills.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground text-center">
            {searchTerm ? 'No skills match your search' : 'No skills available'}
          </div>
        ) : (
          filteredSkills.map(skill => {
            const isSelected = value.includes(skill.optimizerSkillId)
            return (
              <div
                key={skill.optimizerSkillId}
                onClick={() => handleSkillToggle(skill.optimizerSkillId)}
                className={cn(
                  'flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted/50',
                  isSelected && 'bg-primary/10 text-primary'
                )}
              >
                <div className="flex-1">
                  <div className="font-semibold">{skill.name}</div>
                  {skill.category && (
                    <div className="text-sm text-muted-foreground">
                      {skill.category}
                    </div>
                  )}
                </div>
                {skill.requiresCertification && (
                  <span className="text-sm" title="Requires Certification">
                    🏆
                  </span>
                )}
                {isSelected && <Check className="h-4 w-4" />}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// Multi-Select Products Component for VRP Capacity System
interface MultiSelectProductsProps {
  value: Array<{ productId: string; quantity: number }> | undefined
  onValueChange: (value: Array<{ productId: string; quantity: number }>) => void
  projectId: string
  products?: any[] // Products data passed from parent
  placeholder?: string
  className?: string
  disabled?: boolean
  isLoading?: boolean
  maxSelection?: number
  showQuantities?: boolean
}

const MultiSelectProducts = ({
  value = [],
  onValueChange,
  products = [],
  className,
  disabled = false,
  isLoading = false,
  maxSelection = 10,
  showQuantities = true,
}: MultiSelectProductsProps) => {
  const [searchTerm, setSearchTerm] = React.useState('')

  // Transform products data for display
  const productsMap = React.useMemo(() => {
    if (!products || products.length === 0) return new Map()

    const map = new Map()
    products.forEach(product => {
      map.set(product._id, product)
    })
    return map
  }, [products])

  // Filter products based on search
  const filteredProducts = React.useMemo(() => {
    if (!products || products.length === 0) return []

    return products.filter(
      product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.unitType?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [products, searchTerm])

  const selectedProductsDisplay = React.useMemo(() => {
    return value
      .map(item => ({
        ...item,
        product: productsMap.get(item.productId),
      }))
      .filter(item => item.product)
  }, [value, productsMap])

  const handleProductToggle = (productId: string) => {
    const currentValues = value || []
    const existingItem = currentValues.find(
      item => item.productId === productId
    )

    if (existingItem) {
      // Remove product
      onValueChange(currentValues.filter(item => item.productId !== productId))
    } else {
      // Add product (check max limit)
      if (currentValues.length >= maxSelection) {
        return // Don't add if at max
      }
      onValueChange([...currentValues, { productId, quantity: 1 }])
    }
  }

  const handleQuantityChange = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      onValueChange(value.filter(item => item.productId !== productId))
    } else {
      // Update quantity
      onValueChange(
        value.map(item =>
          item.productId === productId ? { ...item, quantity } : item
        )
      )
    }
  }

  if (isLoading) {
    return (
      <div
        className={cn('w-full space-y-4', className)}
        data-slot="multi-select-products-loading"
      >
        <div className="h-4 bg-muted rounded animate-pulse w-32"></div>
        <div className="h-10 bg-muted rounded animate-pulse"></div>
        <div className="h-24 bg-muted rounded animate-pulse"></div>
      </div>
    )
  }

  if (!products || products.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 p-4 border border-dashed border-muted-foreground/20 rounded-md bg-muted/10',
          className
        )}
        role="status"
        aria-label="No products available"
      >
        <span className="text-sm font-normal text-muted-foreground">
          No products defined for this project
        </span>
      </div>
    )
  }

  return (
    <div
      className={cn('w-full space-y-4', className)}
      data-slot="multi-select-products"
    >
      {/* Selected products display */}
      {selectedProductsDisplay.length > 0 && (
        <div
          className="flex flex-wrap gap-2 p-4 bg-muted/20 rounded-md" // VRP Design System: 8pt grid - 16px padding, 8px gap
          role="region"
          aria-label="Selected products"
        >
          {selectedProductsDisplay.map(item => (
            <div key={item.productId} className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="text-sm font-normal cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors min-h-[32px]" // VRP Design System: Typography + touch target
                onClick={() => handleProductToggle(item.productId)}
                title={`Click to remove • ${item.product.category || 'No category'} • ${item.product.unitType}`}
                aria-label={`Remove ${item.product.name} from selection`}
              >
                {item.product.name}
                {showQuantities && ` (${item.quantity})`}
                <span className="ml-1 text-sm opacity-60" aria-hidden="true">
                  ×
                </span>
              </Badge>
              {showQuantities && (
                <div className="flex items-center gap-1 bg-background rounded border px-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      handleQuantityChange(item.productId, item.quantity - 1)
                    }
                    disabled={disabled}
                    aria-label={`Decrease ${item.product.name} quantity`}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={e =>
                      handleQuantityChange(
                        item.productId,
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="w-12 h-6 text-sm text-center p-1 border-0"
                    min="1"
                    disabled={disabled}
                    aria-label={`${item.product.name} quantity`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      handleQuantityChange(item.productId, item.quantity + 1)
                    }
                    disabled={disabled}
                    aria-label={`Increase ${item.product.name} quantity`}
                  >
                    +
                  </Button>
                </div>
              )}
            </div>
          ))}
          <Badge variant="outline" className="text-sm font-normal">
            {selectedProductsDisplay.length} of {maxSelection} selected
          </Badge>
        </div>
      )}

      {/* Search input */}
      <div className="space-y-2">
        <label
          htmlFor="product-search"
          className="text-sm font-semibold text-foreground"
        >
          Search Products
        </label>
        <Input
          id="product-search"
          type="text"
          placeholder="Search by name, category, or unit type..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full"
          disabled={disabled}
          aria-describedby="product-search-help"
        />
        {searchTerm && (
          <div
            id="product-search-help"
            className="text-sm font-normal text-muted-foreground"
          >
            Showing {filteredProducts.length} product
            {filteredProducts.length === 1 ? '' : 's'} matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Available products */}
      <div
        className="max-h-48 overflow-y-auto border border-input rounded-md bg-background" // Increased height for better mobile UX
        role="listbox"
        aria-label="Available products"
      >
        {filteredProducts.length === 0 ? (
          <div className="p-4 text-sm font-normal text-muted-foreground text-center">
            {searchTerm
              ? 'No products match your search'
              : 'No products available'}
          </div>
        ) : (
          filteredProducts.map(product => {
            const selectedItem = value.find(
              item => item.productId === product._id
            )
            const isSelected = !!selectedItem
            const isMaxed = !isSelected && value.length >= maxSelection

            return (
              <div
                key={product._id}
                onClick={() => !isMaxed && handleProductToggle(product._id)}
                className={cn(
                  'flex items-center gap-4 p-3 text-sm cursor-pointer transition-colors min-h-[44px]', // VRP Design System: Mobile touch targets
                  isSelected && 'bg-primary/10 text-primary',
                  !isSelected && !isMaxed && 'hover:bg-muted/50',
                  isMaxed && 'opacity-50 cursor-not-allowed'
                )}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isMaxed}
                tabIndex={isMaxed ? -1 : 0}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground truncate">
                    {product.name}
                  </div>{' '}
                  {/* VRP Design System: Typography */}
                  <div className="text-sm font-normal text-muted-foreground flex gap-2 mt-2">
                    {product.category && <span>{product.category}</span>}
                    <span className="font-semibold">({product.unitType})</span>
                    {product.weightPerUnitKg && (
                      <span>{product.weightPerUnitKg}kg</span>
                    )}
                    {product.volumePerUnitM3 && (
                      <span>{product.volumePerUnitM3}m³</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {product.requiresRefrigeration && (
                    <span
                      className="text-sm"
                      title="Requires Refrigeration"
                      aria-label="Requires Refrigeration"
                    >
                      ❄️
                    </span>
                  )}
                  {product.stackable && (
                    <span
                      className="text-sm"
                      title="Stackable"
                      aria-label="Stackable"
                    >
                      📦
                    </span>
                  )}
                  {isSelected && (
                    <Check
                      className="h-4 w-4 text-primary"
                      aria-hidden="true"
                    />
                  )}
                  {isMaxed && (
                    <span
                      className="text-sm font-normal text-muted-foreground"
                      title="Maximum selection reached"
                    >
                      Max
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ProductSelector - Wrapper for SearchableSelect with product-specific logic
interface ProductSelectorProps {
  projectId: string
  value?: string | string[]
  onChange: (value: string | string[]) => void
  multiple?: boolean
  placeholder?: string
  disabled?: boolean
  compartmentTypeFilter?: string
  showProductDetails?: boolean
}

const ProductSelector = ({
  projectId,
  value,
  onChange,
  multiple = false,
  placeholder = 'Select products...',
  disabled = false,
  compartmentTypeFilter,
  showProductDetails = true,
}: ProductSelectorProps) => {
  // Use existing MultiSelectProducts for multiple selection mode
  if (multiple) {
    const multiValue = Array.isArray(value)
      ? value.map(id => ({ productId: id, quantity: 1 }))
      : []
    return (
      <MultiSelectProducts
        value={multiValue}
        onValueChange={newValue =>
          onChange(newValue.map(item => item.productId))
        }
        projectId={projectId}
        placeholder={placeholder}
        disabled={disabled}
        showQuantities={false}
      />
    )
  }

  // For single selection, use SearchableSelect with product transformation
  return (
    <SearchableSelect
      value={value as string}
      onValueChange={onChange as (value: string) => void}
      options={[]} // TODO: Transform products to options
      placeholder={placeholder}
      disabled={disabled}
      isLoading={false}
      loadingMessage="Loading products..."
    />
  )
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SearchableSelect,
  MultiSelectSkills,
  MultiSelectProducts,
  ProductSelector,
  type SearchableSelectOption,
  type SearchableSelectProps,
  type MultiSelectSkillsProps,
  type MultiSelectProductsProps,
  type ProductSelectorProps,
}
